import { useState, useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

import AccountSiderList from "../AccountSiderList";
import { SidebarSkeleton } from "../SidebarSkeleton";
import { useTasklists, useTitleMap } from "@/hooks";
import { LockedAccountSidebar } from "@/components/LockedAccountPlaceholder";
import UpgradeDialog from "@/components/UpgradeDialog";

type SidebarContentProps = {
  accounts: Record<string, any>;
};

const SidebarContent = ({ accounts }: SidebarContentProps) => {
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const unlockedAccounts = useMemo(
    () => Object.fromEntries(Object.entries(accounts).filter(([, acc]) => !acc.locked)),
    [accounts]
  );

  const listQueries = useTasklists(unlockedAccounts);
  const titleMap = useTitleMap();

  const allAccountEntries = Object.entries(accounts);
  const unlockedEntries = Object.entries(unlockedAccounts);

  return (
    <>
      <ScrollArea className="text-sm flex-1">
        {allAccountEntries.map(([accountId, user]) => {
          if (user.locked) {
            return (
              <LockedAccountSidebar
                key={accountId}
                account={user}
                onUpgrade={() => setShowUpgradeDialog(true)}
              />
            );
          }

          // Find the query index based on position in unlocked accounts
          const unlockedIndex = unlockedEntries.findIndex(([id]) => id === accountId);
          const query = listQueries[unlockedIndex];

          if (!query || query.isLoading) {
            return <SidebarSkeleton key={accountId} />;
          }

          return (
            <AccountSiderList
              key={accountId}
              user={user}
              lists={query.data || []}
              listTitleMap={titleMap}
            />
          );
        })}
      </ScrollArea>

      <UpgradeDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog} source="sidebar" />
      <div className="flex flex-col p-4">
        <div className="my-4 mt-auto">
          <p className="text-muted-foreground text-xs">
            Send feedback to{" "}
            <a className="underline" href="mailto: contact@task-fusion.pro">
              contact@task-fusion.pro
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default SidebarContent;
